import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { greetingName } from '../../components/TopBar'
import { Box, Button, Card, Grid, Typography } from '@mui/material'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import AirportShuttleOutlinedIcon from '@mui/icons-material/AirportShuttleOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import SettingsInputCompositeOutlinedIcon from '@mui/icons-material/SettingsInputCompositeOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined'
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import PageTitle from '../../components/PageTitle'
import { getAccess } from '../../helpers/ReduxHelpers'
import palette from '../../../theme/palette'

// ─── D2C Freight modules ─────────────────────────────────────────────────────
const D2C_MODULES = [
  { name: 'D2C Dashboard', desc: 'Overview of D2C shipment volumes, delivery rates & SLA performance', icon: <DashboardOutlinedIcon />, color: palette.primary.main, url: '/dashboard/d2c', roles: ['DASHBOARD', 'B2C_ACCESS'] },
  { name: 'Shipment Management', desc: 'Forward, reverse & channel orders — operations, tracking & downloads', icon: <LocalShippingOutlinedIcon />, color: palette.primary.main, url: '/d2c/orders/forward', roles: ['B2C_ACCESS'] },
  { name: 'Delivery Performance', desc: 'NDR & NPR exception handling, intervention analytics & automation', icon: <WarningAmberOutlinedIcon />, color: palette.error.main, url: '/ndr-npr/ndr/open', roles: ['NDR'] },
  { name: 'Automated Allocation', desc: 'PACE — Prozo Automated Courier Engine: rules, simulation & analytics', icon: <RocketLaunchOutlinedIcon />, color: '#e65100', url: '/pace/allocation', roles: ['TOOLS'] },
  { name: 'Courier Performance', desc: 'Courier benchmarking and delivery SLA tracking', icon: <AssessmentOutlinedIcon />, color: '#6a1b9a', url: '/courier-benchmarking', roles: ['DASHBOARD'] },
  { name: 'Serviceability', desc: 'Pan-India reach, pincode coverage, delivery issues & service types', icon: <TravelExploreOutlinedIcon />, color: '#00695c', url: '/serviceability-intel/trends/overview', roles: ['INTERNAL', 'TOOLS', 'SA'] },
  { name: 'Weight & Discrepancy', desc: 'Weight ledger, merchant vs courier discrepancies, DWS config', icon: <ScaleOutlinedIcon />, color: '#3949ab', url: '/weight/ledger', roles: ['WEIGHT_DESC', 'AP_RESTRICTED'] },
  { name: 'Claims & Insurance', desc: 'Lost, damaged and insurance claim management', icon: <AssignmentLateOutlinedIcon />, color: '#bf360c', url: '/claims/all', roles: ['B2C_ACCESS', 'PTL_ACCESS', 'DASHBOARD', 'INTERNAL', 'TOOLS', 'NDR', 'WEIGHT_DESC', 'SA', 'AP_RESTRICTED'] },
  { name: 'Utilities & Tools', desc: 'Serviceability check, rate calculator, carrier logs, label config', icon: <BuildOutlinedIcon />, color: '#00796b', url: '/tools/serviceability', roles: ['TOOLS', 'ORDER_TERMINAL_RESET'] },
  { name: 'Courier Setup', desc: 'Partner management, pricing, zones, reconciliation & legacy config', icon: <SettingsInputCompositeOutlinedIcon />, color: '#558b2f', url: '/supply/partners', roles: ['INTERNAL', 'TOOLS', 'SA'] },
  { name: 'Merchant Setup', desc: 'Merchants, onboarding, KYC, platform defaults & bulk operations', icon: <StorefrontOutlinedIcon />, color: '#0277bd', url: '/merchant-config/merchants', roles: ['INTERNAL', 'TOOLS', 'FINANCE'] },
  { name: 'Finance & Billing', desc: 'Invoicing, wallet, COD, credit notes & payment management', icon: <AccountBalanceOutlinedIcon />, color: '#4527a0', url: '/finance/wallet', roles: ['FINANCE'] },
  { name: 'Master Data', desc: 'COD remittance, wallet balances, credit notes & notifications', icon: <StorageOutlinedIcon />, color: '#37474f', url: '/masters/cod-remittance-all', roles: ['INTERNAL'] },
  { name: 'Data Uploads', desc: 'Serviceability, PODs, EDD, order history & holiday uploads', icon: <FileUploadOutlinedIcon />, color: '#37474f', url: '/uploads/serviceability', roles: ['INTERNAL'] },
  { name: 'Reports & Analytics', desc: 'Operational reports, financial reports & dashboards', icon: <AssessmentOutlinedIcon />, color: '#6a1b9a', url: '/finance/reports', roles: ['REPORTS'] },
]

// ─── PTL Freight modules ─────────────────────────────────────────────────────
const PTL_MODULES = [
  { name: 'PTL Dashboard', desc: 'Overview of PTL shipment volumes, pickup performance & delivery SLAs', icon: <DashboardOutlinedIcon />, color: '#2e7d32', url: '/dashboard/ptl', roles: ['PTL_ACCESS', 'DASHBOARD'] },
  { name: 'Pickup Requests', desc: 'Quick & detailed pickup requests, all request tracking', icon: <LocalShippingOutlinedIcon />, color: '#2e7d32', url: '/ptl/pickups/all', roles: ['PTL_ACCESS'] },
  { name: 'ABD Shipments', desc: 'Appointment Before Delivery — scheduled deliveries, miss tracking, ABD tagging', icon: <EventNoteOutlinedIcon />, color: '#00695c', url: '/appointments/all', roles: ['PTL_ACCESS'] },
  { name: 'Non-ABD Shipments', desc: 'Standard PTL shipments without appointments — all, open & stuck orders', icon: <AirportShuttleOutlinedIcon />, color: '#2e7d32', url: '/ptl/shipments/non-abd', roles: ['PTL_ACCESS'] },
  { name: 'PTL Operations', desc: 'Manual status updates, tracking uploads, manifestation & additional charges', icon: <BuildOutlinedIcon />, color: '#00796b', url: '/ptl/operations/manual-status-update', roles: ['PTL_ACCESS'] },
  { name: 'PTL Partner Setup', desc: 'B2B courier partner configuration and setup', icon: <SettingsInputCompositeOutlinedIcon />, color: '#558b2f', url: '/supply/ptl', roles: ['INTERNAL', 'TOOLS', 'SA'] },
  { name: 'PTL Merchants', desc: 'B2B merchants — profile, commercials, fulfilment & automation', icon: <StorefrontOutlinedIcon />, color: '#0277bd', url: '/merchant-config/merchants?segment=ptl', roles: ['INTERNAL', 'TOOLS', 'FINANCE'] },
]

// ─── Admin modules ───────────────────────────────────────────────────────────
const ADMIN_MODULES = [
  { name: 'User & Access', desc: 'User management, PII access control, audit trails & system config', icon: <ManageAccountsOutlinedIcon />, color: '#4e342e', url: '/users/management', roles: ['USER_SAVE', 'USER_RO', 'SA'] },
]

// ─── Module card ─────────────────────────────────────────────────────────────

function ModuleCard({ group }) {
  return (
    <Card
      component={Link}
      to={group.url}
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: '14px 16px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: palette.grey[300],
        borderLeft: `4px solid ${group.color}`,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        textDecoration: 'none',
        height: '100%',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': {
          boxShadow: `0 4px 14px ${group.color}22`,
          transform: 'translateY(-1px)',
          borderColor: group.color,
        },
      }}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: '8px',
        bgcolor: `${group.color}14`, color: group.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, '& svg': { fontSize: 20 },
      }}>
        {group.icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: palette.text.primary, lineHeight: 1.3, mb: '3px' }}>
          {group.name}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: palette.text.secondary, lineHeight: 1.5 }}>
          {group.desc}
        </Typography>
      </Box>
    </Card>
  )
}

// ─── Section header ──────────────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{
        width: 42, height: 42, borderRadius: '10px',
        background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        '& svg': { fontSize: 24 },
        boxShadow: `0 2px 8px ${color}44`,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '18px', fontWeight: 800, color: palette.text.primary }}>{title}</Typography>
        <Typography sx={{ fontSize: '12px', color: palette.text.secondary, mt: '1px' }}>{subtitle}</Typography>
      </Box>
    </Box>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Home() {
  const { userInfo } = useSelector((state) => state.user)
  // Person-first greeting (QA BUG-05) — the token fullname is per-account; the mapped
  // merchant record can carry a different user's name on shared admin merchants.
  const merchantName = greetingName(userInfo) || userInfo?.username || 'User'
  const accessList = getAccess()

  const hasAccess = (roles) => roles.some((r) => accessList.includes(r))
  const d2cVisible = D2C_MODULES.filter((g) => hasAccess(g.roles))
  const ptlVisible = PTL_MODULES.filter((g) => hasAccess(g.roles))
  const adminVisible = ADMIN_MODULES.filter((g) => hasAccess(g.roles))

  return (
    <>
      <PageTitle>Home</PageTitle>

      {/* ── Hero Banner ───────────────────────────────────────────────────── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0d1b2a 0%, #1b3a5c 60%, #0d2137 100%)',
        borderRadius: '12px',
        p: '28px 36px',
        mb: 3,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', right: -60, top: -60,
          width: 280, height: 280, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Typography sx={{ fontSize: '24px', fontWeight: 800, color: '#fff', mb: '8px' }}>
          Welcome back, {merchantName}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', maxWidth: 620, lineHeight: 1.7 }}>
          ProShip — Multi-carrier shipping aggregation, NDR & NPR management, PTL freight, appointment deliveries,
          weight management, and finance — everything you need to run your supply chain operations.
        </Typography>
      </Box>

      {/* ── D2C FREIGHT ───────────────────────────────────────────────────── */}
      {d2cVisible.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            icon={<LocalShippingOutlinedIcon />}
            title='D2C Freight'
            subtitle='Direct-to-Consumer shipment management, delivery performance & operations'
            color={palette.primary.main}
          />
          <Grid container spacing={1.75}>
            {d2cVisible.map((group) => (
              <Grid item xs={12} sm={6} md={3} key={group.name}>
                <ModuleCard group={group} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ── PTL FREIGHT ───────────────────────────────────────────────────── */}
      {ptlVisible.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            icon={<AirportShuttleOutlinedIcon />}
            title='PTL Freight'
            subtitle='Part Truck Load shipments — ABD & non-ABD deliveries, pickups & operations'
            color='#2e7d32'
          />
          <Grid container spacing={1.75}>
            {ptlVisible.map((group) => (
              <Grid item xs={12} sm={6} md={3} key={group.name}>
                <ModuleCard group={group} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ── ADMIN ─────────────────────────────────────────────────────────── */}
      {adminVisible.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            icon={<ManageAccountsOutlinedIcon />}
            title='Administration'
            subtitle='User management, system configuration & audit trails'
            color='#4e342e'
          />
          <Grid container spacing={1.75}>
            {adminVisible.map((group) => (
              <Grid item xs={12} sm={6} md={3} key={group.name}>
                <ModuleCard group={group} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  )
}
